import React from 'react';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { ExpansionArtworkId, getExpansionArtwork, getExpansionIcon } from '../utils/expansionArtwork';

interface ExpansionTogglesProps {
  includeBaseCards?: boolean;
  enableDunwichBoard?: boolean;
  enableKingsportBoard?: boolean;
  enableInnsmouthBoard?: boolean;
  onEnableDunwichBoardChange?: (value: boolean) => void;
  onEnableKingsportBoardChange?: (value: boolean) => void;
  onEnableInnsmouthBoardChange?: (value: boolean) => void;
  includeDunwichCards: boolean;
  includeDarkPharaohCards: boolean;
  includeKingsportCards: boolean;
  includeKingInYellowCards: boolean;
  includeBlackGoatCards: boolean;
  includeMiskatonicCards?: boolean;
  includeLurkerCards?: boolean;
  includeInsmouthCards?: boolean;
  includeFanMadeInvestigators: boolean;
  disableBaseCards?: boolean;
  showAdminOnly?: boolean;
  onIncludeBaseCardsChange?: (value: boolean) => void;
  onIncludeDunwichCardsChange: (value: boolean) => void;
  onIncludeDarkPharaohCardsChange: (value: boolean) => void;
  onIncludeKingsportCardsChange: (value: boolean) => void;
  onIncludeKingInYellowCardsChange: (value: boolean) => void;
  onIncludeBlackGoatCardsChange: (value: boolean) => void;
  onIncludeMiskatonicCardsChange?: (value: boolean) => void;
  onIncludeLurkerCardsChange?: (value: boolean) => void;
  onIncludeInsmouthCardsChange?: (value: boolean) => void;
  onIncludeFanMadeInvestigatorsChange: (value: boolean) => void;
}

interface ExpansionToggleCardProps {
  id: string;
  controlLabel: string;
  imageAlt: string;
  caption: string;
  expansion: ExpansionArtworkId;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}

const ExpansionToggleCard: React.FC<ExpansionToggleCardProps> = ({
  id,
  controlLabel,
  imageAlt,
  caption,
  expansion,
  checked,
  disabled = false,
  onChange,
}) => {
  const style = useMonsterTokenStyle();
  const digital = style === 'Digital';
  return (
    <label
      className={`expansion-toggle-card${digital ? ' expansion-toggle-digital' : ''}${checked ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
      htmlFor={id}
    >
      <input
        id={id}
        className="expansion-toggle-input"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={controlLabel}
      />
      <span className="expansion-toggle-image-wrapper">
        <img src={getExpansionArtwork(expansion, style)} alt={imageAlt} className="expansion-toggle-image" />
        {digital && <span className="expansion-toggle-emblem" aria-hidden="true">
          <img src={getExpansionIcon(expansion, style)} alt="" />
        </span>}
        <span className="expansion-toggle-checkmark" aria-hidden="true" />
      </span>
      <span className="expansion-toggle-label">{caption}</span>
    </label>
  );
};

export const ExpansionToggles: React.FC<ExpansionTogglesProps> = ({
  includeBaseCards = true,
  enableDunwichBoard = true,
  enableKingsportBoard = true,
  enableInnsmouthBoard = true,
  onEnableDunwichBoardChange,
  onEnableKingsportBoardChange,
  onEnableInnsmouthBoardChange,
  includeDunwichCards,
  includeDarkPharaohCards,
  includeKingsportCards,
  includeKingInYellowCards,
  includeBlackGoatCards,
  includeMiskatonicCards = false,
  includeLurkerCards = false,
  includeInsmouthCards,
  includeFanMadeInvestigators,
  disableBaseCards = false,
  showAdminOnly = false,
  onIncludeBaseCardsChange,
  onIncludeDunwichCardsChange,
  onIncludeDarkPharaohCardsChange,
  onIncludeKingsportCardsChange,
  onIncludeKingInYellowCardsChange,
  onIncludeBlackGoatCardsChange,
  onIncludeMiskatonicCardsChange,
  onIncludeLurkerCardsChange,
  onIncludeInsmouthCardsChange,
  onIncludeFanMadeInvestigatorsChange,
}) => {
  const editableToggles = [
    { checked: includeBaseCards, onChange: disableBaseCards ? undefined : onIncludeBaseCardsChange },
    { checked: includeDunwichCards, onChange: onIncludeDunwichCardsChange },
    { checked: includeDarkPharaohCards, onChange: onIncludeDarkPharaohCardsChange },
    { checked: includeKingsportCards, onChange: onIncludeKingsportCardsChange },
    { checked: includeKingInYellowCards, onChange: onIncludeKingInYellowCardsChange },
    { checked: includeBlackGoatCards, onChange: onIncludeBlackGoatCardsChange },
    { checked: includeInsmouthCards, onChange: includeInsmouthCards !== undefined ? onIncludeInsmouthCardsChange : undefined },
    { checked: includeLurkerCards, onChange: onIncludeLurkerCardsChange },
    { checked: includeMiskatonicCards, onChange: onIncludeMiskatonicCardsChange },
    { checked: includeFanMadeInvestigators, onChange: onIncludeFanMadeInvestigatorsChange },
  ].filter(toggle => toggle.onChange !== undefined);
  const allSelected = editableToggles.length > 0 && editableToggles.every(toggle => toggle.checked);

  return (
    <section className="expansions-section" aria-label="Expansions">
      <h3 className="expansions-title">Expansions</h3>
      <button
        type="button"
        className={`expansion-bulk-button${allSelected ? ' all-selected' : ''}`}
        disabled={editableToggles.length === 0}
        onClick={() => editableToggles.forEach(toggle => toggle.onChange?.(!allSelected))}
      >
        <span className="expansion-bulk-icons" aria-hidden="true">
          <span className="expansion-toggle-checkmark" />
          <span className="expansion-toggle-checkmark" />
          <span className="expansion-toggle-checkmark" />
        </span>
        <span>{allSelected ? 'Disable All' : 'Enable All'}</span>
      </button>
      <div className="expansions-row">
        <ExpansionToggleCard
          id="include-base-cards-toggle"
          controlLabel="Base expansion"
          imageAlt="Arkham Horror base game"
          caption="Base Game"
          expansion="Base"
          checked={includeBaseCards}
          disabled={disableBaseCards}
          onChange={onIncludeBaseCardsChange ?? (() => undefined)}
        />

        <div className="expansion-toggle-with-board">
          <ExpansionToggleCard
            id="include-dunwich-cards-toggle"
            controlLabel="Dunwich expansion"
            imageAlt="Dunwich Horror"
            caption="Dunwich Horror"
            expansion="Dunwich"
            checked={includeDunwichCards}
            onChange={onIncludeDunwichCardsChange}
          />
          {includeDunwichCards && onEnableDunwichBoardChange && (
            <label className="expansion-board-option">
              <input type="checkbox" checked={enableDunwichBoard} onChange={event => onEnableDunwichBoardChange(event.target.checked)} />
              Enable Dunwich board
            </label>
          )}
        </div>
        
        <ExpansionToggleCard
          id="include-dark-pharaoh-cards-toggle"
          controlLabel="Dark Pharaoh cards"
          imageAlt="Curse of the Dark Pharaoh"
          caption="Curse of the Dark Pharaoh"
          expansion="DarkPharaoh"
          checked={includeDarkPharaohCards}
          onChange={onIncludeDarkPharaohCardsChange}
        />

        <div className="expansion-toggle-with-board">
          <ExpansionToggleCard
            id="include-kingsport-cards-toggle"
            controlLabel="Kingsport expansion"
            imageAlt="Kingsport Horror"
            caption="Kingsport Horror"
            expansion="Kingsport"
            checked={includeKingsportCards}
            onChange={onIncludeKingsportCardsChange}
          />
          {includeKingsportCards && onEnableKingsportBoardChange && (
            <label className="expansion-board-option">
              <input type="checkbox" checked={enableKingsportBoard} onChange={event => onEnableKingsportBoardChange(event.target.checked)} />
              Enable Kingsport board
            </label>
          )}
        </div>

        <ExpansionToggleCard
          id="include-king-in-yellow-cards-toggle"
          controlLabel="The King in Yellow expansion"
          imageAlt="The King in Yellow"
          caption="The King in Yellow"
          expansion="KingInYellow"
          checked={includeKingInYellowCards}
          onChange={onIncludeKingInYellowCardsChange}
        />

        <ExpansionToggleCard
          id="include-black-goat-cards-toggle"
          controlLabel="The Black Goat of the Woods expansion"
          imageAlt="The Black Goat of the Woods"
          caption="The Black Goat of the Woods"
          expansion="BlackGoat"
          checked={includeBlackGoatCards}
          onChange={onIncludeBlackGoatCardsChange}
        />

        {includeInsmouthCards !== undefined && onIncludeInsmouthCardsChange && (
          <div className="expansion-toggle-with-board">
            <ExpansionToggleCard
              id="include-insmouth-cards-toggle"
              controlLabel="Innsmouth expansion"
              imageAlt="Innsmouth Horror"
              caption="Innsmouth Horror"
              expansion="Innsmouth"
              checked={includeInsmouthCards}
              onChange={onIncludeInsmouthCardsChange}
            />
            {includeInsmouthCards && onEnableInnsmouthBoardChange && (
              <label className="expansion-board-option">
                <input type="checkbox" checked={enableInnsmouthBoard} onChange={event => onEnableInnsmouthBoardChange(event.target.checked)} />
                Enable Innsmouth board
              </label>
            )}
          </div>
        )}

        <ExpansionToggleCard
          id="include-lurker-cards-toggle"
          controlLabel="The Lurker at the Threshold expansion"
          imageAlt="The Lurker at the Threshold"
          caption="The Lurker at the Threshold"
          expansion="Lurker"
          checked={includeLurkerCards}
          onChange={onIncludeLurkerCardsChange ?? (() => undefined)}
        />

        <ExpansionToggleCard
          id="include-miskatonic-cards-toggle"
          controlLabel="Miskatonic expansion"
          imageAlt="Miskatonic Horror"
          caption="Miskatonic Horror"
          expansion="Miskatonic"
          checked={includeMiskatonicCards}
          onChange={onIncludeMiskatonicCardsChange ?? (() => undefined)}
        />

        <ExpansionToggleCard
          id="include-fan-made-investigators-toggle"
          controlLabel="Fan Made Investigators expansion"
          imageAlt="Fan Made Investigators"
          caption="Fan Made Investigators"
          expansion="FanMade"
          checked={includeFanMadeInvestigators}
          onChange={onIncludeFanMadeInvestigatorsChange}
        />
      </div>
    </section>
  );
};
